/* Canadian postal code validation + email format check + IP-capture helpers. */

const CA_POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Rough list of obviously-bad domains; keep short to avoid false positives.
const BAD_EMAIL_DOMAINS = new Set([
  "example.com", "test.com", "foo.com", "bar.com", "asdf.com",
  "gmal.com", "gamil.com", "gmial.com", "yaho.com", "hotnail.com",
]);

export function validateEmail(value) {
  const v = (value || "").trim();
  if (!v) return { ok: false, error: "Email required." };
  if (!EMAIL_RE.test(v)) return { ok: false, error: "Enter a valid email address." };
  const domain = v.split("@")[1].toLowerCase();
  if (BAD_EMAIL_DOMAINS.has(domain))
    return { ok: false, error: `Please enter a real email (did you mean @${domain.replace(/gmal|gamil|gmial/,'gmail').replace(/yaho/, 'yahoo').replace(/hotnail/, 'hotmail')}?).` };
  return { ok: true };
}

export function validatePostal(value, country) {
  const v = (value || "").trim();
  if (!v) return { ok: false, error: "Postal code required." };
  if ((country || "").toLowerCase() === "canada") {
    if (!CA_POSTAL_RE.test(v))
      return { ok: false, error: "Canadian postal code format: A1A 1A1." };
  } else {
    if (v.length < 3) return { ok: false, error: "Postal / ZIP code too short." };
  }
  return { ok: true };
}

// Per-country expected national-number length (E.164 digits sans country code).
// Sources: ITU-T E.164 country plans + Twilio SMS prefix rules. Codes missing
// from this table fall through to the permissive "≥ 7 digits" rule.
const PHONE_LENGTHS_BY_CC = {
  "+1":    10,         // CA / US / JM (NANP)
  "+1876": [7, 10],    // Jamaica — historic 7-digit local, 10 with area code
  "+20":   10,         // Egypt
  "+27":   9,          // South Africa
  "+31":   9,          // Netherlands
  "+33":   9,          // France
  "+34":   9,          // Spain
  "+39":   [9, 10],    // Italy
  "+40":   9,          // Romania
  "+44":   [9, 10],    // UK
  "+48":   9,          // Poland
  "+49":   [10, 11],   // Germany
  "+51":   9,          // Peru
  "+52":   10,         // Mexico
  "+55":   [10, 11],   // Brazil
  "+57":   10,         // Colombia
  "+60":   [9, 10],    // Malaysia
  "+61":   9,          // Australia
  "+62":   [9, 10, 11],// Indonesia
  "+63":   10,         // Philippines
  "+64":   [8, 9],     // New Zealand
  "+65":   8,          // Singapore
  "+66":   9,          // Thailand
  "+81":   [9, 10],    // Japan
  "+82":   [9, 10],    // South Korea
  "+84":   9,          // Vietnam
  "+86":   11,         // China
  "+90":   10,         // Turkey
  "+91":   10,         // India
  "+92":   10,         // Pakistan
  "+94":   [9, 10],    // Sri Lanka
  "+212":  9,          // Morocco
  "+233":  9,          // Ghana
  "+234":  [10, 11],   // Nigeria
  "+254":  9,          // Kenya
  "+255":  9,          // Tanzania
  "+256":  9,          // Uganda
  "+351":  9,          // Portugal
  "+353":  9,          // Ireland
  "+380":  9,          // Ukraine
  "+852":  8,          // Hong Kong
  "+880":  10,         // Bangladesh
  "+886":  9,          // Taiwan
  "+966":  9,          // Saudi Arabia
  "+971":  9,          // UAE
  "+972":  9,          // Israel
  "+977":  10,         // Nepal
};

export function validatePhone(value, countryCode = "+1") {
  let digits = (value || "").replace(/\D/g, "");
  if (!digits) return { ok: false, error: "Phone number required." };

  // E.164 normalisation: if the user (or a previous form step) typed the
  // dial code into the phone-number field itself (e.g. "+16478934909"),
  // strip it before length-checking against the per-country rule.
  const ccDigits = (countryCode || "").replace(/\D/g, "");
  if (ccDigits && digits.startsWith(ccDigits) && digits.length > ccDigits.length) {
    digits = digits.slice(ccDigits.length);
  }

  const expected = PHONE_LENGTHS_BY_CC[countryCode];
  if (expected) {
    const okLen = Array.isArray(expected) ? expected.includes(digits.length) : digits.length === expected;
    if (!okLen) {
      const want = Array.isArray(expected) ? expected.join(" or ") : expected;
      return { ok: false, error: `Enter a valid ${countryCode} phone number (${want} digits).` };
    }
    // North American Numbering Plan — area code can't start with 0 or 1.
    if (countryCode === "+1" && /^[01]/.test(digits)) {
      return { ok: false, error: "Phone area code can't start with 0 or 1." };
    }
    return { ok: true };
  }
  // Unknown country code — fall back to a permissive 7-digit minimum so we
  // don't break edge cases for codes outside the supported list.
  if (digits.length < 7) return { ok: false, error: "Enter a valid phone number." };
  return { ok: true };
}

export function validateName(label, value) {
  if (!(value || "").trim()) return { ok: false, error: `${label} required.` };
  return { ok: true };
}

/**
 * Sentence-case (a.k.a. Title Case) a person's name.
 * Lowercases the whole string then upper-cases the first letter of every
 * word, including parts after hyphens and apostrophes.
 * Examples:
 *   "tim"          → "Tim"
 *   "TIM SMITH"    → "Tim Smith"
 *   "mary-jane"    → "Mary-Jane"
 *   "o'brien"      → "O'Brien"
 *   "  john  doe " → "John Doe"
 */
export function toTitleCaseName(value) {
  if (!value) return "";
  const s = String(value).trim().replace(/\s+/g, " ").toLowerCase();
  return s.replace(/(^|[\s\-'])([a-zà-ÿ])/g, (_m, sep, ch) => sep + ch.toUpperCase());
}
